import React, { useEffect, useState, useRef, useContext } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BallTriangle } from 'react-loader-spinner';
import Nodatafound from 'pages/Common/Nodatafound';
import DailyTestQuestion from './component/Index';
import nft1 from 'assets/img/nfts/NftBanner1.png';
import toast from 'react-hot-toast';
import testService from 'ApiService/Student/testServices';

const Index = ({ fetchTests, NoData }) => {
  const [test, setTest] = useState([]);
  const { state } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false); //warning
  const [restrictedActionsCount, setRestrictedActionsCount] = useState(0); //warning
  const [selectedClass, setSelectedClass] = useState(null);
  const [isFullScreenExitAttempt, setIsFullScreenExitAttempt] = useState(false);
  const [tabSwitchDetected, setTabSwitchDetected] = useState(false);
  const [testStatus, setTestStatus] = useState({});
  const [filteredTests, setFilteredTests] = useState([]);
  const [filterOption, setFilterOption] = useState('all');
  const testContainerRef = useRef(null);

  const navigate = useNavigate();

  const [childSubmitHandler, setChildSubmitHandler] = useState(null);

  const handleSetChildSubmit = (submitFn) => {
    console.log('Setting child submit handler', submitFn);
    setChildSubmitHandler(() => submitFn);
  };

  const handleRestrictedAction = async () => {
    setRestrictedActionsCount((prev) => prev + 1);

    if (restrictedActionsCount === 0) {
      // Show warning on the first occurrence
      setShowWarningPopup(true);
      toast.error('Warning: Avoid switching tabs or exiting full-screen mode.');
    } else if (restrictedActionsCount >= 1) {
      // End test on the second or subsequent occurrences
      toast.error('Multiple rule violations detected. Test will be submitted.');

      // Exit full-screen mode
      if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }

      // Use childSubmitHandler instead of onSetHandleSubmit
      if (childSubmitHandler && typeof childSubmitHandler === 'function') {
        setShowWarningPopup(false);
        await childSubmitHandler();
      }
    }
  };

  const fetchtest = async () => {
    setLoading(true);
    try {
      const response = await fetchTests();
      if (response.data.success) {
        const tests = response.data.tests;
        setTest(tests);
        setFilteredTests(tests);
        const testIds = tests.map((test) => test._id);
        await fetchTestStatus(testIds);
      }
    } catch (error) {
      console.error('Failed to fetch tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestStatus = async (testIds) => {
    try {
      const response = await testService.getTestStatus(testIds);

      if (response.data.success) {
        const statusMap = response.data.testStatus || {};

        // Extract statuses from the nested structure
        const mappedStatuses = Object.keys(statusMap).reduce((acc, testId) => {
          const testStatus = statusMap[testId];
          acc[testId] = testStatus.success ? testStatus.status : 'not started';
          return acc;
        }, {});

        setTestStatus((prevStatus) => ({
          ...prevStatus,
          ...mappedStatuses,
        }));
      } else {
        console.error('Failed to fetch test statuses:', response.data.message);
      }
    } catch (error) {
      console.error('Failed to fetch test statuses:', error);
    }
  };

  const handleFullScreenChange = () => {
    if (!document.fullscreenElement && showDetails) {
      // Full-screen exit attempt
      setIsFullScreenExitAttempt(true);
    }
  };

  const handleExitConfirmation = (confirmExit) => {
    if (confirmExit) {
      // If user confirms, submit the test
      toast.error('Exited full-screen mode. Submitting the test...');
      if (childSubmitHandler && typeof childSubmitHandler === 'function') {
        childSubmitHandler();
      }
    } else {
      // User canceled, go back to full-screen mode
      if (testContainerRef.current.requestFullscreen) {
        testContainerRef.current.requestFullscreen();
      }
    }
    setIsFullScreenExitAttempt(false);
  };

  // const handleAttendanceClick = async (classItem) => {
  //   setSelectedClass(classItem || {});
  //   setShowWarningPopup(true);
  // };

  const handleAttendanceClick = async (classItem) => {
    try {
      const response = await testService.startTest({ testId: classItem._id });

      if (response.data.success) {
        if (response.data.isStarted) {
          setSelectedClass(classItem || {});
          setShowWarningPopup(true);
        } else {
          toast.error(response.data.message);
        }
      } else {
        toast.error(response.data.message);
      }
      // else {
      //   const { daysLeft, hoursLeft, minutesLeft } = response.data;
      //   toast.error(
      //     `Test has not started yet. Time left: ${
      //       daysLeft > 0 ? `${daysLeft} days` : ''
      //     } ${hoursLeft} hours and ${minutesLeft} minutes.`
      //   );
      // }
    } catch (error) {
      console.error('Failed to start test:', error);
      toast.error('An error occurred while starting the test.');
    }
  };

  const handleBackClick = () => {
    setSelectedClass(null);
    setShowDetails(false);
    // setShowWarningPopup(false);

    // Exit full-screen mode
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch((err) => {
        console.error('Error exiting full-screen mode:', err);
      });
    }
  };

  const handleTabSwitchContinue = () => {
    setTabSwitchDetected(false);

    // Re-enter full-screen mode
    if (testContainerRef.current) {
      testContainerRef.current.requestFullscreen().catch((err) => {
        console.error('Error entering full-screen mode:', err);
      });
    }
  };

  useEffect(() => {
    if (state?.user) {
      fetchtest();
    }

    // Add event listener for full-screen changes
    document.addEventListener('fullscreenchange', handleFullScreenChange);

    // Add a "beforeunload" listener to warn users against refreshing
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        'Refreshing or leaving this page will end your test. Are you sure?';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state?.user, showDetails]);

  useEffect(() => {
    // Listener to handle visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden && showDetails) {
        // Detect tab switch and show modal
        // setTabSwitchDetected(true);
        handleRestrictedAction();

        // Attempt to re-enter full screen
        if (testContainerRef.current) {
          testContainerRef.current.requestFullscreen().catch((err) => {
            console.error('Error entering full-screen mode:', err);
          });
        }
      }
    };

    // Listener to re-enable full-screen if exited
    const handleFullScreenChange = () => {
      if (!document.fullscreenElement && showDetails) {
        handleRestrictedAction();

        // Re-enter full-screen mode
        testContainerRef.current?.requestFullscreen().catch((err) => {
          console.error('Error entering full-screen mode:', err);
        });
      }
    };

    // Block key combinations for tab switching
    const handleKeyDown = (event) => {
      const forbiddenKeys = ['Alt', 'Tab', 'Control'];
      if (showDetails && forbiddenKeys.includes(event.key)) {
        event.preventDefault();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDetails, childSubmitHandler]);

  const handleAcknowledgeWarning = async () => {
    setShowWarningPopup(false);
    setShowDetails(true); // Show test details only after the popup is acknowledged

    // Enable full-screen mode
    if (testContainerRef.current.requestFullscreen) {
      await testContainerRef.current.requestFullscreen();
    }
  };

  const handleFilterChange = (event) => {
    const selectedFilter = event.target.value;
    setFilterOption(selectedFilter);

    if (selectedFilter === 'all') {
      setFilteredTests(test);
    } else {
      const filtered = test.filter((t) => t.type === selectedFilter);

      if (filtered.length === 0) {
        toast.error(
          `No tests available for ${selectedFilter.toUpperCase()} type.`
        );
        setFilteredTests(test);
      } else {
        setFilteredTests(filtered);
      }
    }
  };

  function formatCompletionTime(minutes) {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hr${hours > 1 ? 's' : ''}`;
      } else {
        return `${hours} hr${hours > 1 ? 's' : ''} ${remainingMinutes} min`;
      }
    } else {
      return `${minutes} min`;
    }
  }

  function formatStartTime(time) {
    if (!time) return 'Invalid Time';
    const [hours, minutes] = time.split(':').map(Number);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${suffix}`;
  }

  return (
    <>
      <select
        id="filter"
        value={filterOption}
        onChange={handleFilterChange}
        className="mt-5 w-[100px] cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
      >
        <option value="all">All</option>
        <option value="mcq">MCQ</option>
        <option value="qa">QA</option>
      </select>
      <div
        className="mt-4 h-full w-full rounded-[20px] md:mt-5"
        ref={testContainerRef}
      >
        {/* Tab Switch Detection Modal */}
        {/* Warning Popup */}
        {showWarningPopup && (
          <div className="bg-black fixed inset-0 z-[100] flex items-center justify-center bg-opacity-50">
            <div className="relative w-full max-w-md rounded-lg bg-white p-6 text-center shadow-lg dark:bg-navy-700 dark:shadow-2xl">
              {/* Close icon only before test starts */}
              {!showDetails && (
                <button
                  onClick={() => setShowWarningPopup(false)}
                  className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}

              <p className="mb-4 text-lg font-bold text-red-500">Warning:</p>
              <ul className="mb-6 list-inside list-disc text-sm text-gray-700">
                <li>Do not refresh the page.</li>
                <li>Do not switch tabs.</li>
                <li>Do not exit full-screen mode.</li>
              </ul>
              <p className="mb-4 text-sm text-gray-700">
                Doing any of these actions will end the test immediately.
              </p>
              <button
                onClick={handleAcknowledgeWarning}
                className="rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
              >
                Got It
              </button>
            </div>
          </div>
        )}

        {/* {tabSwitchDetected && (
          <div className="bg-black fixed inset-0 z-[100] flex items-center justify-center bg-opacity-50">
            <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-lg">
              <h2 className="mb-4 text-xl font-bold text-red-500">
                Test Interruption Detected!
              </h2>
              <p className="mb-4 text-lg">
                You cannot switch tabs during the test. Please continue in the
                test window.
              </p>
              <button
                onClick={handleTabSwitchContinue}
                className="rounded-lg bg-blue-500 px-6 py-2 text-white hover:bg-blue-600 focus:outline-none"
              >
                Continue Test
              </button>
            </div>
          </div>
        )} */}
        {showDetails ? (
          <div>
            <DailyTestQuestion
              handleBackClick={handleBackClick}
              question={selectedClass}
              onSetHandleSubmit={handleSetChildSubmit}
            />

            {/* Full-Screen Exit Confirmation Modal */}
            {isFullScreenExitAttempt && (
              <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
                <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-lg">
                  <p className="mb-4 text-lg">
                    Are you sure you want to exit full-screen? Your test will be
                    submitted.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={() => handleExitConfirmation(true)}
                      className="rounded-lg bg-green-500 px-4 py-2 text-white hover:bg-green-600 focus:outline-none"
                    >
                      Yes, Submit Test
                    </button>
                    <button
                      onClick={() => handleExitConfirmation(false)}
                      className="rounded-lg bg-red-500 px-4 py-2 text-white hover:bg-red-600 focus:outline-none"
                    >
                      No, Stay in Full-Screen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {loading ? (
              <div className="flex h-40 items-center justify-center">
                <BallTriangle color="#5431ed" />
              </div>
            ) : filteredTests.length === 0 ? (
              <div className="flex h-40 items-center justify-center dark:text-gray-600">
                <Nodatafound title={NoData} />
              </div>
            ) : (
              <div className="grid h-full w-full grid-cols-1 gap-4 p-2 md:grid-cols-2 lg:grid-cols-3">
                {filteredTests.map((branch) => (
                  <span
                    key={branch._id}
                    className="relative flex h-full cursor-pointer flex-col items-center rounded-xl border border-gray-200 dark:border-gray-700 dark:!bg-navy-800 md:flex-row"
                  >
                    {/* Content Section */}
                    <div className="justify flex h-full w-full flex-col p-4 leading-normal md:w-2/3">
                      <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                        {branch.title}
                      </h5>
                      <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                        {/* Time: {branch.completionTime} */}
                        Completion Time :{' '}
                        {formatCompletionTime(branch.completionTime)}
                      </p>

                      <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                        Total Questions: {branch.questions.length}
                      </p>
                      <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                        Scheduled At:
                        {branch.scheduledAt}
                      </p>
                      <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                        Start Time:
                        {formatStartTime(branch.startTime)}
                      </p>

                      {testStatus[branch._id] === 'completed' ? (
                        <button
                          className="rounded bg-gray-700 px-4 py-2 text-white"
                          disabled
                        >
                          Completed
                        </button>
                      ) : testStatus[branch._id] === 'in_progress' ? (
                        <button
                          className="rounded bg-yellow-500 px-4 py-2 text-white"
                          onClick={() => handleAttendanceClick(branch)}
                        >
                          Continue Test
                        </button>
                      ) : (
                        <button
                          className="rounded bg-blue-500 px-4 py-2 text-white"
                          onClick={() => handleAttendanceClick(branch)}
                        >
                          Start Test
                        </button>
                      )}
                    </div>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Index;
